// HeroLiveDecisions — outcome-driven hero for cold traffic.
// Goal: in <3s, a new visitor sees BUY / WATCH / AVOID on real Polymarket
// markets and can run a full read in one tap. No URL required.

import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type HeroRow = {
  market_id: string;
  market_url: string | null;
  question: string;
  decision: string | null;
  edge_pct: number | null;
  best_option_edge: number | null;
  is_sample?: boolean;
};

// Static fallback so the hero NEVER paints empty for cold ads traffic.
// Marked is_sample so we never falsely present these as live.
const FALLBACK: HeroRow[] = [
  {
    market_id: "sample-buy",
    market_url: null,
    question: "Will the Fed cut rates at the next FOMC meeting?",
    decision: "STRONG",
    edge_pct: 0.07,
    best_option_edge: 0.07,
    is_sample: true,
  },
  {
    market_id: "sample-watch",
    market_url: null,
    question: "Will Bitcoin close above $100k this month?",
    decision: "CONSIDER",
    edge_pct: 0.03,
    best_option_edge: 0.03,
    is_sample: true,
  },
  {
    market_id: "sample-avoid",
    market_url: null,
    question: "Will a major AI company IPO this quarter?",
    decision: "PASS",
    edge_pct: -0.04,
    best_option_edge: -0.04,
    is_sample: true,
  },
];

function verbFor(decision: string | null, edge: number | null): "BUY" | "WATCH" | "AVOID" {
  const d = (decision ?? "").toUpperCase();
  if (d === "STRONG") return "BUY";
  if (d === "CONSIDER") return "WATCH";
  if (d === "PASS") return "AVOID";
  if ((edge ?? 0) >= 0.04) return "BUY";
  if ((edge ?? 0) <= -0.03) return "AVOID";
  return "WATCH";
}

function verbStyles(verb: "BUY" | "WATCH" | "AVOID") {
  if (verb === "BUY") {
    return {
      pill: "bg-primary text-primary-foreground",
      ring: "border-primary/30 hover:border-primary/60 bg-gradient-to-br from-primary/10 to-card",
    };
  }
  if (verb === "AVOID") {
    return {
      pill: "bg-accent text-accent-foreground",
      ring: "border-accent/30 hover:border-accent/60 bg-accent/5",
    };
  }
  return {
    pill: "bg-muted text-foreground",
    ring: "border-border/60 hover:border-foreground/30 bg-card",
  };
}

interface Props {
  /** Runs the full prediction (reuses Index.tsx run()). */
  onRun: (marketUrlOrQuestion: string, marketId: string | null) => void;
  loading?: boolean;
}

export default function HeroLiveDecisions({ onRun, loading }: Props) {
  const [rows, setRows] = useState<HeroRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await supabase
          .from("market_feed")
          .select("market_id, market_url, question, decision, edge_pct, best_option_edge")
          .eq("platform", "polymarket")
          .eq("is_actionable", true)
          .eq("route_status", "ok")
          .eq("platform_visibility", "visible")
          .not("market_url", "is", null)
          .gte("end_date", new Date().toISOString())
          .order("ranking_score", { ascending: false, nullsFirst: false })
          .limit(8);

        if (cancelled) return;

        const live = (data ?? []) as HeroRow[];
        // Try to compose a varied trio: 1 BUY-ish, 1 WATCH-ish, 1 AVOID-ish.
        const picked: HeroRow[] = [];
        const want: Array<"BUY" | "WATCH" | "AVOID"> = ["BUY", "WATCH", "AVOID"];
        for (const w of want) {
          const next = live.find(
            (r) => verbFor(r.decision, r.best_option_edge ?? r.edge_pct) === w && !picked.includes(r),
          );
          if (next) picked.push(next);
        }
        // Fill remaining slots from top-ranked live rows.
        for (const r of live) {
          if (picked.length >= 3) break;
          if (!picked.includes(r)) picked.push(r);
        }
        // Backfill from FALLBACK if we still don't have 3.
        for (const f of FALLBACK) {
          if (picked.length >= 3) break;
          picked.push(f);
        }

        setRows(picked.slice(0, 3));
      } catch {
        if (!cancelled) setRows(FALLBACK);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const display = rows ?? FALLBACK;
  const topLive = (rows ?? []).find((r) => !r.is_sample && r.market_url);

  const handleCardClick = (r: HeroRow) => {
    if (r.is_sample || !r.market_url) return;
    onRun(r.market_url, r.market_id);
  };

  const handleCta = () => {
    if (topLive?.market_url) {
      onRun(topLive.market_url, topLive.market_id);
      return;
    }
    // No live rows yet — pull the single best actionable row on demand.
    void (async () => {
      try {
        const { data } = await supabase
          .from("market_feed")
          .select("market_url, market_id")
          .eq("platform", "polymarket")
          .eq("is_actionable", true)
          .eq("route_status", "ok")
          .not("market_url", "is", null)
          .order("ranking_score", { ascending: false, nullsFirst: false })
          .limit(1)
          .maybeSingle();
        if (data?.market_url) onRun(data.market_url, data.market_id ?? null);
      } catch {
        /* swallow */
      }
    })();
  };

  return (
    <section className="mb-4">
      <header className="text-center mb-5 sm:mb-7">
        <h1 className="text-[28px] sm:text-5xl font-display font-bold text-foreground leading-[1.05] tracking-tight max-w-3xl mx-auto">
          Your 24/7 <span className="text-primary">Polymarket</span> edge desk.
        </h1>
        <p className="text-sm sm:text-base font-body text-muted-foreground mt-3 max-w-xl mx-auto leading-snug">
          Decyzon scans live markets, finds mispricings, shows the move, and alerts you when the price is worth touching.
        </p>
        <p className="text-[11px] font-body text-muted-foreground/80 mt-2">
          Polymarket shows the odds. Decyzon shows the move.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {display.map((r) => {
          const edge = r.best_option_edge ?? r.edge_pct ?? 0;
          const verb = verbFor(r.decision, edge);
          const styles = verbStyles(verb);
          const clickable = !r.is_sample && r.market_url;
          return (
            <button
              key={r.market_id}
              type="button"
              onClick={() => handleCardClick(r)}
              disabled={!clickable || loading}
              className={`text-left rounded-xl border p-3.5 sm:p-4 transition-all ${styles.ring} ${
                clickable
                  ? "cursor-pointer hover:-translate-y-0.5"
                  : "cursor-default opacity-70"
              }`}
            >
              <div className="flex items-center justify-between mb-2.5">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-display font-bold tracking-wider ${styles.pill}`}
                >
                  {verb}
                </span>
                <span className="text-[10.5px] font-body uppercase tracking-wider text-muted-foreground tabular-nums">
                  {r.is_sample
                    ? "Sample"
                    : verb === "BUY"
                    ? `${edge >= 0 ? "+" : ""}${(edge * 100).toFixed(1)}% edge`
                    : verb === "AVOID"
                    ? "Trap risk"
                    : "Watch only"}
                </span>
              </div>
              <p className="text-[13px] sm:text-sm font-body text-foreground leading-snug line-clamp-2 min-h-[36px]">
                {r.question}
              </p>
              {clickable && (
                <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-body uppercase tracking-wider text-primary">
                  Run full read <ArrowRight className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleCta}
        disabled={loading}
        className="w-full sm:max-w-sm sm:mx-auto sm:flex inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-body font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Reading the market…
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" /> See today's best edge
          </>
        )}
      </button>
      <p className="text-center text-[10.5px] font-body text-muted-foreground mt-2">
        Free. No signup needed. Live Polymarket data.
      </p>
    </section>
  );
}
